// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap, type components } from "@granite/api-client";

import { api } from "./api.js";

export type Vault = components["schemas"]["Vault"];

const cacheKey = "granite.vaults";

function cached(): Vault[] | null {
  try {
    const raw = localStorage.getItem(cacheKey);
    return raw ? (JSON.parse(raw) as Vault[]) : null;
  } catch {
    return null;
  }
}

/**
 * The user's vaults. The last list is remembered so the vaults on this device can still be
 * opened offline; it is only used when the server cannot be reached.
 */
export async function listVaults(): Promise<{ items: Vault[]; offline: boolean }> {
  try {
    const { items } = unwrap(await api.GET("/v1/vaults"));
    try {
      localStorage.setItem(cacheKey, JSON.stringify(items));
    } catch {
      // storage blocked
    }
    return { items, offline: false };
  } catch (err) {
    const list = cached();
    if (err instanceof TypeError && list) return { items: list, offline: true };
    throw err;
  }
}

/** One vault (from the server, or from the remembered list when offline). */
export async function getVault(id: string): Promise<Vault> {
  try {
    return unwrap(await api.GET("/v1/vaults/{vault_id}", { params: { path: { vault_id: id } } }));
  } catch (err) {
    const v = cached()?.find((x) => x.id === id);
    if (err instanceof TypeError && v) return v;
    throw err;
  }
}

export function forgetVaultList(): void {
  try {
    localStorage.removeItem(cacheKey);
  } catch {
    // storage blocked
  }
}
